import { gql, useApolloClient } from '@apollo/client'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { useDataLayer } from '../../hooks/useDataLayer'
import { useLang } from '../../hooks/useLang'
import { useTranslations } from '../../hooks/useTranslations'
import { runConditionsCheck } from '../molecules/DynamicFormField'
import DynamicForm from '../organisms/DynamicForm'
import FormProgressOverlay from '../organisms/FormProgressOverlay'

const M22FormBuilder = ({
  heading,
  formieForm,
  formieFormFr,
  formieFormIt,
  anchorLinkId,
}) => {
  const [formState, setFormState] = useState('default')
  const dataLayer = useDataLayer()

  const formMap = {
    de: formieForm,
    fr: formieFormFr,
    it: formieFormIt,
  }
  const locale = useLang()
  const translations = useTranslations()
  const form =
    locale in formMap && formMap[locale] ? formMap[locale] : formieForm
  const apolloClient = useApolloClient()

  const getInitialValues = () =>
    form[0].fields.reduce((prev, cur) => {
      const res = { ...prev }

      if (cur.fields && cur.fields.length > 0) {
        cur.fields
          .filter(field => field.required)
          .forEach(field => {
            res[field.handle] = ''
          })
      }

      if (cur.handle) {
        if (cur.__typename === 'CraftContent_Field_Checkboxes') {
          const defaultValue = cur.options.find(({ isDefault }) => isDefault)

          res[cur.handle] = defaultValue ? defaultValue.value : []
        } else if (cur.__typename === 'CraftContent_Field_Radio') {
          const defaultValue = cur.options.find(({ isDefault }) => isDefault)

          res[cur.handle] = defaultValue ? defaultValue.value : ''
        } else if (cur.__typename === 'CraftContent_Field_FileUpload') {
          res[cur.handle] = []
        } else {
          res[cur.handle] = ''
        }
      }

      return res
    }, {})

  const handleSubmit = async (values, { resetForm }) => {
    setFormState('loading')

    try {
      const types = form[0].fields
        .reduce((prev, cur) => {
          let res = `${prev} `

          if (cur.handle) {
            res += `$${cur.handle}: ${cur.inputTypeName}`
          }

          return res
        }, '')
        .trim()

      const mutationValues = form[0].fields
        .filter(field => !!field.handle)
        .map(field => {
          return `${field.handle}: $${field.handle}`
        })
        .join(' ')

      const variables = {}
      form[0].fields.forEach(async field => {
        if (field.handle) {
          if (field.fields && field.fields.length > 0) {
            const innerValues = {}
            field.fields.forEach(innerField => {
              innerValues[innerField.handle] = values[innerField.handle]
            })

            variables[field.handle] = innerValues

            return
          }

          variables[field.handle] = values[field.handle]
        }
      })

      await apolloClient.mutate({
        mutation: gql`
        mutation FormMutation(${types}) {
          save_${form[0].handle}_Submission(${mutationValues}) {
            id
          }
        }
      `,
        variables,
      })

      dataLayer.push({
        event: 'userinteraction',
        eventCategory: 'Formulare',
        eventAction: 'submit',
        eventLabel: heading,
        event_location: null,
      })
      setFormState('success')
      resetForm({ values: getInitialValues() })

      document.querySelectorAll('select').forEach(select => (select.value = ''))
    } catch (e) {
      setFormState('error')
    }
  }

  const initialValues = getInitialValues()

  const validationSchema = Yup.object().shape(
    form[0].fields.reduce((prev, cur) => {
      const res = { ...prev }

      if (cur.fields && cur.fields.length > 0) {
        cur.fields
          .filter(field => field.required)
          .forEach(field => {
            res[field.handle] = Yup.string().test(
              'required',
              (value, context) => {
                const shouldShow = field.conditions
                  ? runConditionsCheck(
                      JSON.parse(field.conditions),
                      context.parent,
                    )
                  : true

                if (!shouldShow) {
                  return true
                }

                return !!value
              },
            )
          })
      }

      if (cur.handle && cur.__typename === 'CraftContent_Field_FileUpload') {
        res[cur.handle] = Yup.array().test('file', value => {
          const isNotSupport = value?.some(({ filename }) => {
            const parts = filename.split('.')
            const ext = parts[parts.length - 1]

            return !['pdf', 'mp4', 'avi', 'mov'].includes(ext)
          })

          return !isNotSupport
        })
      } else if (cur.handle && cur.__typename === 'CraftContent_Field_Email') {
        res[cur.handle] = Yup.string()
          .email('email')
          .test('required', (value, context) => {
            if (!cur.required) {
              return true
            }

            const shouldShow = cur.conditions
              ? runConditionsCheck(JSON.parse(cur.conditions), context.parent)
              : true

            if (!shouldShow) {
              return true
            }

            return !!value
          })
      } else if (
        cur.handle &&
        cur.required &&
        cur.__typename === 'CraftContent_Field_Checkboxes'
      ) {
        res[cur.handle] = Yup.array().test('required', (value, context) => {
          const shouldShow = cur.conditions
            ? runConditionsCheck(JSON.parse(cur.conditions), context.parent)
            : true

          if (!shouldShow) {
            return true
          }

          return value.length > 0
        })
      } else if (cur.handle && cur.required) {
        res[cur.handle] = Yup.string().test('required', (value, context) => {
          const shouldShow = cur.conditions
            ? runConditionsCheck(JSON.parse(cur.conditions), context.parent)
            : true

          if (!shouldShow) {
            return true
          }

          return !!value
        })
      }

      return res
    }, {}),
  )

  return (
    <>
      <FormProgressOverlay
        state={formState}
        onClose={() => setFormState('default')}
      />
      <section className="my-10 overflow-hidden md:my-20" id={anchorLinkId}>
        <div className="container">
          <div className="row-type-g">
            <div className="col-1-type-g3">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ dirty, errors, handleReset, isSubmitting }) => (
                  <Form id={form[0].handle} encType="multipart/form-data">
                    <div className={`column-indent-both `}>
                      <div
                        className={`transition ${
                          isSubmitting && 'opacity-30 pointer-events-none'
                        }`}
                      >
                        <DynamicForm fields={form[0].fields} />
                      </div>

                      <div className="flex items-center justify-between mt-12">
                        <p className="text-red u-p text-[14px]">
                          {Object.keys(errors ?? {}).length > 0 &&
                            translations.formValidation}
                        </p>
                        <div className="flex items-center justify-end space-x-6">
                          <button
                            type="button"
                            className="underline transition u-navi"
                            onClick={handleReset}
                          >
                            {translations.cancel}
                          </button>
                          <button
                            type="submit"
                            className={`px-4 py-3 text-white ${
                              dirty ? 'bg-black' : 'bg-gray-5'
                            } u-navi rounded-[3px] relative`}
                          >
                            <span className="transition opacity-100">
                              {translations.send}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default M22FormBuilder
